import React, { MouseEvent, useState, useEffect } from 'react';
import { gameVote, fetchGames } from '../../features/player/gameSlice'
import { fetchGameStatus } from '../../features/player/gameStatusSlice'
import { IGameStatus, IMove, IRFI, IRoleContent } from '../../interfaces';
import { InjectVote } from '../../Classes1';
import VotePercentage from './VotePercentage';
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { isEmpty } from 'lodash';

import Modal from 'react-modal';

interface RFIVoteItemProps {
    currentGameStatus: IGameStatus; 
    rfi: IRFI;
    intervalHandler: (command: string, gameId: string, inGame: boolean) => void
    iVotedRFI: boolean;
}

const customStyles = {
    content: {
      background: '#323544',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      padding: '0',
      border: 'none',
      width: '640px',
      transform: 'translate(-50%, -50%)',
    }
  };

  Modal.setAppElement('#root');

  const RFIVoteItem: React.FC<RFIVoteItemProps> = ({ rfi, currentGameStatus, iVotedRFI, intervalHandler }) => {
    const { currentInjectId, gameId, moves, currentTurn, players } = currentGameStatus;
    const playerCount = players.length;

    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.userId)

    const [votingModalIsOpen, setVotingModalIsOpen] = useState<boolean>(false);
    const [rejectedVoteIsOpen, setRejectedVoteIsOpen] = useState<boolean>(false);
    const [voteReason, setVoteReason] = useState<string>('');
    const [voteCount, setVoteCount] = useState<number>(0);
    const [votePercentage, setVotePercentage] = useState<number>(0);
    const [voteReasonError, setVoteReasonError] = useState<boolean>(false);
    const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
    const [rfiChoice, setRfiChoice] = useState<string>('');

    useEffect(() => {
        const rfiMoves: IMove[] = moves.filter(
            (mv) => mv.turn === currentTurn && mv.options?.choiceType === 'rfi');

        const rfiVoteCount = rfiMoves.reduce<number>((tally, mv) =>
            tally + ((mv.type === 'action' && parseInt(mv.choice, 10) === rfi.id) ? 1 : 0)
        , 0);
        setVoteCount(rfiVoteCount)
        setVotePercentage((rfiVoteCount / playerCount))

        setRfiChoice(rfiMoves.find(
            (mv) => mv.type === 'result' && mv.options !== null)?.choice || '')
    }, [currentGameStatus.moves]) // eslint-disable-line react-hooks/exhaustive-deps

    const openVotingModal = () => {
        setVotingModalIsOpen(true);
    }

    const closeVotingModal = () => {
        setVotingModalIsOpen(false);
    }

    const clickBigVote = (ev: MouseEvent) => {
        openVotingModal()
    }

    const openRejectedVote = () => {
        setRejectedVoteIsOpen(true);
    }

    const closeRejectedVote = () => {
        setRejectedVoteIsOpen(false);
    }

    const submitVote = async (ev: MouseEvent) => {

        if (disableSubmit) {
            return;
        }

        const target = ev.currentTarget as HTMLTextAreaElement;

        if (target) {

            if (isEmpty(voteReason)) {
                // show reason error
                setVoteReasonError(true)
                
            } else {
                setDisableSubmit(true)
                let gameVoteObj: any = {};
                gameVoteObj.gameId = gameId;
                gameVoteObj.voteObj = {};
                let newVote;

                newVote = new InjectVote();
                newVote.type = 'action';
                newVote.choice = rfi.id.toString();
                newVote.reason = voteReason;
                newVote.actionOptions = {
                    injectId: currentInjectId!.toString(),
                    choiceType: 'rfi'
                }
                
                gameVoteObj.voteObj = newVote;

                const gameVoteResponse: any = await dispatch(gameVote(gameVoteObj));
                if (gameVoteResponse.error !== undefined) {
                    openRejectedVote();
                }

                closeVotingModal();
                setVoteReason('');
                setDisableSubmit(false);

                await dispatch(fetchGameStatus(gameId));
                await dispatch(fetchGames());
                intervalHandler("start", gameId, true);
            }
        }
    }

    const onChangeReason = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        const target = ev.currentTarget as HTMLTextAreaElement;

        if (target) {
            setVoteReason(target.value)
        }
    }

    const clickReasonTextarea = (ev: MouseEvent) => {
        ev.stopPropagation();
    }

    const RFIAnswers = () => {
        if (rfi.id !== parseInt(rfiChoice, 10)) {
            return <></>
        }

        const superPlayer = (playerCount === 1)
        const playerRole = players.find((p) => p.userID === user)?.role;
        const availableContent: IRoleContent[] = superPlayer
                ? rfi.roleContent
                : rfi.roleContent.filter((content) => content.role === "All" || content.role === playerRole);

        return <div className="game-rfi-answer-text">
            <div className="game-rfi-label-a">A</div>
            {availableContent.length === 0
            ? <div>No Information Available for your Role</div>
            : availableContent.map((rc, idx) => <div key={`${rc.role}:${idx}`}>
                {rc.displayText}
                {superPlayer && <b> {rc.role === 'All' ? '' : `(Visible Only To ${rc.role})`} </b>}
            </div>)}
        </div>
    }

    return (
        <div>
            <div className={`game-vote-item ${iVotedRFI ? 'already-voted' : ''}`} onClick={iVotedRFI ? undefined : clickBigVote}>
                    <div className="game-rfi-item-title">
                        RFI
                    </div>
                    <div className="game-vote-item-details">
                        <div className="game-rfi-question-text">
                            <div className="game-rfi-label-q">Q</div>
                            {rfi.displayText}
                        </div>
                        <RFIAnswers />
                        <div className="game-vote-item-votes">
                            <VotePercentage iVoted={false} iVotedRFI={iVotedRFI} voteCount={voteCount} votePercentage={votePercentage} />
                        </div>
                    </div>
                </div>

            <Modal
                isOpen={votingModalIsOpen}
                onRequestClose={closeVotingModal}
                style={customStyles}
                shouldCloseOnOverlayClick={true}
                contentLabel="Voting Modal"
            >
                <div className="vote-modal-header">Vote Reasoning <img src="/img/light-grey-x.svg" className="new-modal-padding-close" alt="x" onClick={closeVotingModal}></img></div>

                <div className="vote-modal-text">Please describe your reasoning for selecting this RFI.</div>

                { voteReasonError ? <div className="vote-reason-error">Vote reason required</div> : <></> }
                <textarea className="vote-reason-text" value={voteReason} onChange={onChangeReason} onClick={clickReasonTextarea}></textarea>

                <button onClick={closeVotingModal} className="new-cancel-btn"><span>Cancel</span></button>
                <button onClick={submitVote} className="new-confirm-btn"><span>Submit</span></button>
            </Modal>

            <Modal
                isOpen={rejectedVoteIsOpen}
                onRequestClose={closeRejectedVote}
                style={customStyles}
                contentLabel="Rejected Vote Modal"
            >
                <div className="game-over-message">You already voted for this inject.</div>
                <button onClick={closeRejectedVote} className="join-game delete-cancel-btn">Close</button>
            </Modal>

        </div>
    )

  }

  export default RFIVoteItem